import { useCallback, useEffect, useState } from "react";
import { getPushPublicKey, removePushSubscription, savePushSubscription } from "../lib/api.ts";
import {
  getGameToken,
  getPushAutoEnabled,
  setPushAutoEnabled,
  setPushPreference,
} from "../lib/storage.ts";

export type PushStatus = "unsupported" | "idle" | "loading" | "subscribed" | "denied" | "error";

function applicationServerKey(value: string): Uint8Array<ArrayBuffer> {
  const normalized = value.replace(/-/g, "+").replace(/_/g, "/");
  const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, "=");
  const raw = atob(padded);
  const buffer = new ArrayBuffer(raw.length);
  const bytes = new Uint8Array(buffer);
  for (let index = 0; index < raw.length; index += 1) bytes[index] = raw.charCodeAt(index);
  return bytes;
}

function pushSupported(): boolean {
  return "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}

export function usePushNotifications(gameId: string, player: boolean) {
  const [status, setStatus] = useState<PushStatus>(() => (pushSupported() ? "loading" : "unsupported"));
  const [error, setError] = useState<string | null>(null);
  const isiOS = /iphone|ipad|ipod/i.test(navigator.userAgent);
  const standalone = window.matchMedia("(display-mode: standalone)").matches ||
    Boolean((navigator as Navigator & { standalone?: boolean }).standalone);

  useEffect(() => {
    if (!player || !pushSupported()) return;
    if (Notification.permission === "denied") {
      setStatus("denied");
      return;
    }
    if (Notification.permission !== "granted" || !getPushAutoEnabled()) {
      setStatus("idle");
      return;
    }

    let cancelled = false;
    void navigator.serviceWorker.ready
      .then(async (registration) => {
        const token = getGameToken(gameId);
        if (!token) {
          if (!cancelled) setStatus("idle");
          return;
        }
        const existing = await registration.pushManager.getSubscription();
        const subscription = existing ?? await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: applicationServerKey(await getPushPublicKey()),
        });
        await savePushSubscription(gameId, token, subscription.toJSON());
        if (!cancelled) {
          setPushPreference(gameId, true);
          setStatus("subscribed");
        }
      })
      .catch(() => {
        if (!cancelled) setStatus("error");
      });
    return () => {
      cancelled = true;
    };
  }, [gameId, player]);

  const enable = useCallback(async () => {
    if (!player || !pushSupported()) return;
    setStatus("loading");
    setError(null);
    try {
      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        setStatus("denied");
        setError("Разрешение на уведомления не выдано.");
        return;
      }
      const token = getGameToken(gameId);
      if (!token) throw new Error("Место игрока не найдено на этом устройстве.");
      const registration = await navigator.serviceWorker.ready;
      const publicKey = await getPushPublicKey();
      const subscription = await registration.pushManager.getSubscription() ??
        await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: applicationServerKey(publicKey),
        });
      await savePushSubscription(gameId, token, subscription.toJSON());
      setPushAutoEnabled(true);
      setPushPreference(gameId, true);
      setStatus("subscribed");
    } catch (caught) {
      setStatus("error");
      setError(caught instanceof Error ? caught.message : "Не удалось включить уведомления.");
    }
  }, [gameId, player]);

  const disable = useCallback(async () => {
    if (!player || !pushSupported()) return;
    setStatus("loading");
    setError(null);
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      const token = getGameToken(gameId);
      if (subscription && token) await removePushSubscription(gameId, token, subscription.endpoint);
      // Подписка браузера может одновременно обслуживать профильные приглашения.
      // Здесь отключаем только уведомления этой партии, не весь PWA.
      setPushAutoEnabled(false);
      setPushPreference(gameId, false);
      setStatus("idle");
    } catch (caught) {
      setStatus("error");
      setError(caught instanceof Error ? caught.message : "Не удалось отключить уведомления.");
    }
  }, [gameId, player]);

  return {
    status,
    error,
    needsIosInstall: isiOS && !standalone,
    enable,
    disable,
  };
}
